import { useState, useEffect } from 'react'
import { Container, GridWrapper, GridItem } from '@sainsburys-tech/luna-grid'
import Error from '../components/Error'
import ComponentSwitch from '../components/Reporting/ComponentSwitch'
import getAvailableComponents from '../components/Reporting/ComponentSwitch/getAvailableComponents'
import ReportingErrorBoundary from '../components/Reporting/ReportingErrorBoundary'
import { ComponentContextProvider } from '../components/Reporting/ComponentSwitch/ComponentContext'
import Loading from '../components/Loading'
import useCurrentStore from '../hooks/useCurrentStore'
import useReportingReducer, {
  back,
  cancel,
  next,
  resetMetadata,
  init,
  updateStoreCode,
  updateAnswer,
  updateMetadata
} from '../hooks/useReportingReducer'
import { useApiClient } from '../context/AppContext'
import { useParams } from 'react-router'

const ReportingPage = () => {
  const store = useCurrentStore()
  const apiClient = useApiClient()
  const [nextDisabledCounter, setNextDisabledCounter] = useState(0)
  const [state, dispatch] = useReportingReducer(apiClient)

  const { transactionId } = useParams()

  useEffect(() => {
    document.title = 'PFS'
  }, [])

  useEffect(() => {
    if (store.storeCode) {
      dispatch(updateStoreCode(store.storeCode))
    }
  }, [store.storeCode])

  useEffect(() => {
    const sessionId = sessionStorage.getItem('sessionId') || null
    dispatch(init(sessionId, { transactionId: transactionId }))
  }, [])

  useEffect(() => {
    if (state.sessionId) {
      sessionStorage.setItem('sessionId', state.sessionId)
    }
  }, [state.sessionId])

  useEffect(() => {
    if (state.loading) {
      disableNext()
    } else {
      enableNext()
    }
  }, [state.loading])

  const handleGetAnswer = ({ id }: any) => {
    return state.answers[id]
  }

  const handleGetAnswers = ({ ids }: any) => {
    return ids.map((id: any) => state.answers[id])
  }

  const handleUpdate = ({ id, value }: { id: any; value: any }) => {
    dispatch(updateAnswer(id, value))
  }

  const handleNext = (overrides: any) => {
    dispatch(next(overrides))
  }

  const handleUpdateMetadata = ({ id, value }: { id: any; value: any }) => {
    dispatch(updateMetadata(id, value))
  }

  const handleResetMetadata = () => {
    dispatch(resetMetadata())
  }

  const handleCancel = () => {
    dispatch(cancel())
  }

  const handleBack = () => {
    dispatch(back())
  }

  const disableNext = () => {
    setNextDisabledCounter(count => count + 1)
  }

  const enableNext = () => {
    setNextDisabledCounter(count => count - 1)
  }

  const isNextDisabled = () => nextDisabledCounter > 0

  if (!state?.components && state.loading) return <Loading />

  if (state.error) {
    return (
      <Container className="s-page-push ln-u-text-align-center ln-u-vertical-align-center">
        <Error
          message={state?.error?.message || 'An error has occurred'}
          tryAgain={() => window.location.reload()}
        />
      </Container>
    )
  }
  return (
    <Container size="xs" className="s-page-push">
      <ReportingErrorBoundary>
        <ComponentContextProvider
          availableComponents={getAvailableComponents()}
          answers={state.answers || {}}
          handleNext={handleNext}
          handleUpdate={handleUpdate}
          handleUpdateMetadata={handleUpdateMetadata}
          handleResetMetadata={handleResetMetadata}
          handleGetAnswer={handleGetAnswer}
          handleGetAnswers={handleGetAnswers}
          handleCancel={handleCancel}
          handleBack={handleBack}
          isNextDisabled={isNextDisabled}
          enableNext={enableNext}
          disableNext={disableNext}
        >
          <GridWrapper
            matrix
            gutterSize="zero"
            className="ln-o-grid--gutter-zero@max-mobile"
          >
            {state.components?.map((componentProps: any) => (
              <GridItem
                key={`${state.serviceType}-${state.id}-${componentProps.id}`}
                size="1/1"
              >
                <ComponentSwitch
                  {...componentProps}
                  key={`${state.serviceType}-${state.id}`}
                />
              </GridItem>
            ))}
          </GridWrapper>
        </ComponentContextProvider>
      </ReportingErrorBoundary>
    </Container>
  )
}

export default ReportingPage
